import { memo, useEffect, useRef } from "react";
import { Form, InputField, Text, useForm, useTheme } from "react-better-html";

import { useI18n } from "../../../utils/i18n";
import { useReduxAction, useSelectedOrganization } from "../../../utils/hooks";

import Section from "../../../components/structure/Section";

function OrganizationSection() {
   const theme = useTheme();
   const { t } = useI18n();
   const selectedOrganization = useSelectedOrganization();

   const formSetValuesRef = useRef<boolean>(false);

   const editOrganization = useReduxAction((actions) => actions.organizations.editOrganization);

   const form = useForm({
      defaultValues: {
         name: selectedOrganization?.name ?? "",
         description: selectedOrganization?.description ?? "",
         phone: selectedOrganization?.phone ?? "",
         email: selectedOrganization?.email ?? "",
         website: selectedOrganization?.website ?? "",
      },
      requiredFields: ["name"],
      onSubmit: (values) => {
         if (!selectedOrganization) return;

         editOrganization({
            id: selectedOrganization.id,
            data: {
               name: values.name,
            },
            onSuccess: (response) => {
               form.setDefaultFieldsValue({
                  name: response.name,
               });
            },
         });
      },
   });

   useEffect(() => {
      if (!selectedOrganization) return;
      if (formSetValuesRef.current) return;

      form.setDefaultFieldsValue({
         name: selectedOrganization.name,
      });
      form.setFieldsValue({
         name: selectedOrganization.name,
      });

      formSetValuesRef.current = true;
   }, [selectedOrganization]);

   return selectedOrganization && selectedOrganization.userRole === "OWNER" ? (
      <Section
         title={t((l) => l.pages.settings.sections.organization.title)}
         description={t((l) => l.pages.settings.sections.organization.description)}
      >
         <Form
            form={form}
            gap={theme.styles.gap}
            submitButtonText={t((l) => l.common.save)}
            submitButtonLoaderName=""
            submitButtonIsDisabled={!form.isDirty}
            renderActionButtons={
               !form.isDirty ? (
                  <Text fontSize={14} color={theme.colors.textSecondary} marginLeft={theme.styles.gap}>
                     {t((l) => l.components.form.nothingToSave)}
                  </Text>
               ) : undefined
            }
         >
            <InputField
               label={t((l) => l.pages.settings.sections.organization.form.fields.organizationName)}
               backgroundColor={theme.colors.backgroundBase}
               {...form.getInputFieldProps("name")}
            />
            <InputField.multiline
               label={t((l) => l.common.description)}
               backgroundColor={theme.colors.backgroundBase}
               {...form.getTextAreaProps("description")}
            />
            <InputField.email
               label={t((l) => l.pages.settings.sections.organization.form.fields.contactEmail)}
               placeholder="contact@email.here"
               backgroundColor={theme.colors.backgroundBase}
               {...form.getInputFieldProps("email")}
            />
            <InputField.phoneNumber
               label={t((l) => l.pages.settings.sections.organization.form.fields.contactPhone)}
               backgroundColor={theme.colors.backgroundBase}
               {...form.getInputFieldProps("phone")}
            />
            <InputField
               label={t((l) => l.pages.settings.sections.organization.form.fields.website)}
               placeholder="https://..."
               backgroundColor={theme.colors.backgroundBase}
               {...form.getInputFieldProps("website")}
            />
         </Form>
      </Section>
   ) : undefined;
}

export default memo(OrganizationSection);
